/**
 * Project page state: shell project row, Overview metrics, tickets, documents.
 *
 * Data-loading strategy (production hardening):
 * - Project detail + Overview metrics load once per project id.
 * - Metrics Type/Range filters are client-side (no refetch).
 * - Metrics requests use AbortController + one transient-network retry.
 * - Ticket/document loads are independent surfaces with their own error flags.
 * - Heavy Schedule/Dispatch UIs are lazy-mounted by ProjectPage tabs (not here).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { getProjectConcretePours } from "../../../services/api/dispatch";
import {
  getJobDocumentFolders,
  getJobFolderPath,
} from "../../../services/api/files";
import { getProject } from "../../../services/api/projects";
import { getRFIProjectSummaries } from "../../../services/api/rfi";
import { getProjectPayoutHours } from "../../../services/api/timekeeping";
import { getTMProjectSummaries } from "../../../services/api/tmlog";
import {
  DEFAULT_PROJECT_TAB,
  EMPTY_TICKET_SUMMARY,
  PROJECT_PAGE_PLACEHOLDER_CONTENT,
  PROJECT_TABS,
} from "../constants";
import {
  buildDocumentFolderLinkPlaceholders,
  getEmptyDocumentFoldersState,
} from "./projectDocumentFolders";
import {
  aggregateConcretePoursChartDays,
  aggregateConcretePoursEntries,
  aggregateWorkerHoursEntries,
  buildConcretePoursMetrics,
  buildWorkerHoursMetrics,
  getAllOverviewMetricsSelection,
  getDefaultOverviewMetricsSelection,
  getEntireProjectMetricsBounds,
  getLoadingConcretePoursMetrics,
  getLoadingWorkerHoursMetrics,
  isEntireProjectMetricsRange,
  OVERVIEW_METRICS_TYPE_ALL,
} from "./metrics/metricsHelpers";

const ProjectPageContext = createContext(null);

/**
 * Whether an Axios failure looks like a dropped connection (dev reload / blip).
 *
 * @param {unknown} error
 * @returns {boolean}
 */
function isTransientNetworkError(error) {
  const code = error?.code;
  const message = String(error?.message || "");
  return (
    code === "ERR_NETWORK" ||
    code === "ECONNABORTED" ||
    message === "Network Error" ||
    message.includes("ERR_INVALID_HTTP_RESPONSE")
  );
}

/**
 * Run an async loader once, retrying once after a short delay on network blips.
 *
 * @template T
 * @param {() => Promise<T>} loader
 * @param {AbortSignal} [signal] - Skip retry when the request was cancelled.
 * @returns {Promise<T>}
 */
async function loadWithNetworkRetry(loader, signal) {
  try {
    return await loader();
  } catch (error) {
    if (
      signal?.aborted ||
      error?.code === "ERR_CANCELED" ||
      !isTransientNetworkError(error)
    ) {
      throw error;
    }
    await new Promise((resolve) => {
      window.setTimeout(resolve, 350);
    });
    if (signal?.aborted) {
      throw error;
    }
    return loader();
  }
}

/**
 * Return the empty/loading summary shape used by project ticket cards.
 *
 * @param {boolean} isLoading - Whether the summary is loading.
 * @returns {Object}
 */
function getEmptyTicketSummary(isLoading = false) {
  return {
    items: [],
    isLoading,
    error: null,
  };
}

/**
 * Read the current ProjectPage context value.
 *
 * @returns {Object}
 */
export function useProjectPageContext() {
  const context = useContext(ProjectPageContext);
  if (!context) {
    throw new Error("useProjectPageContext must be used within ProjectPageProvider");
  }
  return context;
}

/**
 * Provide shared state for the project detail page and its tabs.
 *
 * @param {Object} props - Component props.
 * @param {string|number} props.projectId - Backing JobNumber primary key.
 * @param {React.ReactNode} props.children - Child page content.
 * @returns {JSX.Element}
 */
const PROJECT_TAB_IDS = new Set(PROJECT_TABS.map((tab) => tab.id));

/**
 * Resolve a ``?tab=`` query value to a known project tab id.
 *
 * @param {string|null} tabParam
 * @returns {string}
 */
function resolveProjectTab(tabParam) {
  if (tabParam && PROJECT_TAB_IDS.has(tabParam)) {
    return tabParam;
  }
  return DEFAULT_PROJECT_TAB;
}

/**
 * Load one project and share tab, ticket summaries, and document-folder state.
 *
 * @param {Object} props
 * @param {string|number} props.projectId JobNumber PK.
 * @param {import("react").ReactNode} props.children
 * @returns {JSX.Element}
 */
export const ProjectPageProvider = ({ projectId, children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTabState] = useState(() =>
    resolveProjectTab(searchParams.get("tab"))
  );
  const [editingProject, setEditingProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tmTicketSummary, setTmTicketSummary] = useState(EMPTY_TICKET_SUMMARY);
  const [rfiTicketSummary, setRfiTicketSummary] = useState(EMPTY_TICKET_SUMMARY);
  // Curated Egnyte document folders — loaded once per project (keep-alive safe).
  const [documentFolders, setDocumentFolders] = useState(
    getEmptyDocumentFoldersState
  );
  // Full project metrics payloads — loaded once; Type/Range filter client-side.
  const [workerHoursSource, setWorkerHoursSource] = useState(null);
  const [workerHoursLoading, setWorkerHoursLoading] = useState(false);
  const [workerHoursError, setWorkerHoursError] = useState(null);
  // Range starts empty; set to job date_start/date_end when project loads.
  const [workerHoursRange, setWorkerHoursRange] = useState(
    getAllOverviewMetricsSelection
  );
  // Empty array = All types (matches SearchableMultiSelect empty state).
  const [workerHoursTypes, setWorkerHoursTypes] = useState([]);
  const [concretePoursSource, setConcretePoursSource] = useState(null);
  const [concretePoursLoading, setConcretePoursLoading] = useState(false);
  const [concretePoursError, setConcretePoursError] = useState(null);
  const [concretePoursRange, setConcretePoursRange] = useState(
    getAllOverviewMetricsSelection
  );
  // Effective Entire Project bounds (saved dates + any blank-date resolver fills).
  const [entireProjectBounds, setEntireProjectBounds] = useState(null);
  const [concretePoursType, setConcretePoursType] = useState(
    OVERVIEW_METRICS_TYPE_ALL
  );

  /**
   * Set Worker Hours range from a full selection object (preset or custom).
   *
   * @param {{key: string, start: string|null, end: string|null}} selection
   * @returns {void}
   */
  const setWorkerHoursRangeSelection = useCallback((selection) => {
    if (!selection?.key) {
      setWorkerHoursRange(getAllOverviewMetricsSelection());
      return;
    }
    setWorkerHoursRange(selection);
  }, []);

  /**
   * Set Concrete Pours range from a full selection object (preset or custom).
   *
   * @param {{key: string, start: string|null, end: string|null}} selection
   * @returns {void}
   */
  const setConcretePoursRangeSelection = useCallback((selection) => {
    if (!selection?.key) {
      setConcretePoursRange(getAllOverviewMetricsSelection());
      return;
    }
    setConcretePoursRange(selection);
  }, []);

  /**
   * Replace Worker Hours Type multi-select (empty = All).
   *
   * @param {string[]} keys
   * @returns {void}
   */
  const setWorkerHoursTypeKeys = useCallback((keys) => {
    setWorkerHoursTypes(Array.isArray(keys) ? keys.map(String) : []);
  }, []);

  /**
   * Set Concrete Pours Type (exclusive single select).
   *
   * @param {string} key
   * @returns {void}
   */
  const setConcretePoursTypeKey = useCallback((key) => {
    setConcretePoursType(key || OVERVIEW_METRICS_TYPE_ALL);
  }, []);

  // Derive tile metrics from cached source + current filters (no refetch).
  const workerHoursSummary = useMemo(() => {
    const entries = workerHoursSource?.entries || [];
    const workerTypes = workerHoursSource?.workerTypes || [];
    const isEntireProject = isEntireProjectMetricsRange(
      workerHoursRange,
      entireProjectBounds
    );
    if (workerHoursLoading) {
      return {
        data: null,
        metrics: getLoadingWorkerHoursMetrics(
          workerHoursTypes,
          workerTypes,
          isEntireProject
        ),
        isLoading: true,
        error: null,
        range: workerHoursRange,
        type: workerHoursTypes,
        workerTypes,
        entries,
      };
    }
    const data = aggregateWorkerHoursEntries(
      entries,
      workerHoursRange,
      workerHoursTypes
    );
    return {
      data,
      metrics: buildWorkerHoursMetrics(
        data,
        workerHoursTypes,
        workerTypes,
        isEntireProject
      ),
      isLoading: false,
      error: workerHoursError,
      range: workerHoursRange,
      type: workerHoursTypes,
      workerTypes,
      entries,
    };
  }, [
    entireProjectBounds,
    workerHoursSource,
    workerHoursLoading,
    workerHoursError,
    workerHoursRange,
    workerHoursTypes,
  ]);

  const concretePoursSummary = useMemo(() => {
    const entries = concretePoursSource?.entries || [];
    const isEntireProject = isEntireProjectMetricsRange(
      concretePoursRange,
      entireProjectBounds
    );
    if (concretePoursLoading) {
      return {
        data: null,
        chartDays: [],
        metrics: getLoadingConcretePoursMetrics(
          concretePoursType,
          isEntireProject
        ),
        isLoading: true,
        error: null,
        range: concretePoursRange,
        type: concretePoursType,
        entries,
      };
    }
    // Metrics = Type-filtered; chartDays = full Range with in/out Type split.
    const data = aggregateConcretePoursEntries(
      entries,
      concretePoursRange,
      concretePoursType
    );
    const chartDays = aggregateConcretePoursChartDays(
      entries,
      concretePoursRange,
      concretePoursType
    );
    return {
      data,
      chartDays,
      metrics: buildConcretePoursMetrics(
        data,
        concretePoursType,
        isEntireProject
      ),
      isLoading: false,
      error: concretePoursError,
      range: concretePoursRange,
      type: concretePoursType,
      entries,
    };
  }, [
    entireProjectBounds,
    concretePoursSource,
    concretePoursLoading,
    concretePoursError,
    concretePoursRange,
    concretePoursType,
  ]);

  const refreshProject = useCallback(() => {
    setIsLoading(true);
    setError(null);
    return getProject(projectId)
      .then(setProject)
      .catch((requestError) => {
        console.error("Failed to load project", requestError);
        setError("Project could not be loaded. Please return to the project list.");
      })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  useEffect(() => {
    refreshProject();
  }, [refreshProject]);

  useEffect(() => {
    if (!project) {
      setTmTicketSummary(getEmptyTicketSummary());
      setRfiTicketSummary(getEmptyTicketSummary());
      setDocumentFolders(getEmptyDocumentFoldersState());
      return undefined;
    }

    let isCancelled = false;
    setTmTicketSummary(getEmptyTicketSummary(true));
    setRfiTicketSummary(getEmptyTicketSummary(true));

    // Job-scoped slim summaries (not full T&M/RFI catalogs).
    Promise.all([
      getTMProjectSummaries({ jobId: project.id }),
      getRFIProjectSummaries({ jobId: project.id }),
    ])
      .then(([tmLogs, rfis]) => {
        if (isCancelled) return;
        setTmTicketSummary({
          items: tmLogs,
          isLoading: false,
          error: null,
        });
        setRfiTicketSummary({
          items: rfis,
          isLoading: false,
          error: null,
        });
      })
      .catch((requestError) => {
        if (isCancelled) return;
        console.error("Failed to load project ticket summaries", requestError);
        const summaryError = "Ticket summaries could not be loaded.";
        setTmTicketSummary({
          items: [],
          isLoading: false,
          error: summaryError,
        });
        setRfiTicketSummary({
          items: [],
          isLoading: false,
          error: summaryError,
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [project]);

  // Resolve Egnyte job path ASAP for links, then load throttled folder stats once.
  useEffect(() => {
    if (!project?.number) {
      setDocumentFolders(getEmptyDocumentFoldersState());
      return undefined;
    }

    let isCancelled = false;
    setDocumentFolders(getEmptyDocumentFoldersState(true));

    getJobFolderPath(project.number)
      .then((pathPayload) => {
        if (isCancelled) return null;
        if (!pathPayload?.path) {
          setDocumentFolders({
            ...getEmptyDocumentFoldersState(),
            error: "Project folder could not be resolved.",
          });
          return null;
        }

        setDocumentFolders({
          path: pathPayload.path,
          webUrl: pathPayload.webUrl || null,
          folders: buildDocumentFolderLinkPlaceholders(pathPayload.path),
          isLoading: false,
          statsLoading: true,
          error: null,
        });

        return getJobDocumentFolders({ folderPath: pathPayload.path });
      })
      .then((foldersPayload) => {
        if (isCancelled) return;
        if (!foldersPayload?.folders) {
          setDocumentFolders((prev) => ({
            ...prev,
            isLoading: false,
            statsLoading: false,
          }));
          return;
        }
        setDocumentFolders((prev) => ({
          path: foldersPayload.path || prev.path,
          webUrl: foldersPayload.webUrl || prev.webUrl,
          folders: foldersPayload.folders,
          isLoading: false,
          statsLoading: false,
          error: null,
        }));
      })
      .catch((requestError) => {
        if (isCancelled) return;
        console.error("Failed to load project document folders", requestError);
        setDocumentFolders((prev) => ({
          ...prev,
          isLoading: false,
          statsLoading: false,
          error: prev.folders?.length
            ? null
            : "Project files could not be loaded.",
        }));
      });

    return () => {
      isCancelled = true;
    };
  }, [project?.number]);

  // Load full metrics payloads once per project (filters are client-side).
  useEffect(() => {
    if (!project?.id) {
      setWorkerHoursSource(null);
      setWorkerHoursError(null);
      setWorkerHoursLoading(false);
      setConcretePoursSource(null);
      setConcretePoursError(null);
      setConcretePoursLoading(false);
      setEntireProjectBounds(null);
      return undefined;
    }

    setWorkerHoursTypes([]);
    setConcretePoursType(OVERVIEW_METRICS_TYPE_ALL);
    // Saved JobNumber dates first; blank bounds filled after activity loads.
    const initialBounds = getEntireProjectMetricsBounds(project);
    setEntireProjectBounds(initialBounds);
    setWorkerHoursRange(getDefaultOverviewMetricsSelection(project));
    setConcretePoursRange(getDefaultOverviewMetricsSelection(project));

    const controller = new AbortController();
    const { signal } = controller;
    setWorkerHoursLoading(true);
    setWorkerHoursError(null);
    setConcretePoursLoading(true);
    setConcretePoursError(null);

    const hoursPromise = loadWithNetworkRetry(
      () => getProjectPayoutHours({ job: project.id }, { signal }),
      signal
    )
      .then((data) => {
        if (signal.aborted) return null;
        setWorkerHoursSource(data);
        setWorkerHoursLoading(false);
        return data;
      })
      .catch((requestError) => {
        if (signal.aborted || requestError?.code === "ERR_CANCELED") return null;
        console.error("Failed to load project payout hours", requestError);
        setWorkerHoursSource(null);
        setWorkerHoursError("Worker hours could not be loaded.");
        setWorkerHoursLoading(false);
        return null;
      });

    const poursPromise = loadWithNetworkRetry(
      () => getProjectConcretePours({ job: project.id }, { signal }),
      signal
    )
      .then((data) => {
        if (signal.aborted) return null;
        setConcretePoursSource(data);
        setConcretePoursLoading(false);
        return data;
      })
      .catch((requestError) => {
        if (signal.aborted || requestError?.code === "ERR_CANCELED") return null;
        console.error("Failed to load project concrete pours", requestError);
        setConcretePoursSource(null);
        setConcretePoursError("Concrete pours could not be loaded.");
        setConcretePoursLoading(false);
        return null;
      });

    // Store resolved Entire Project bounds; fill blank JobNumber dates from activity.
    Promise.all([hoursPromise, poursPromise]).then(([hoursData, poursData]) => {
      if (signal.aborted) return;
      const entries = [
        ...(hoursData?.entries || []),
        ...(poursData?.entries || []),
      ];
      const bounds = getEntireProjectMetricsBounds(project, entries);
      setEntireProjectBounds(bounds);
      if (!project.date_start || !project.date_end) {
        const range = getDefaultOverviewMetricsSelection(project, entries);
        setWorkerHoursRange(range);
        setConcretePoursRange(range);
      }
    });

    return () => {
      controller.abort();
    };
  }, [project]);

  /**
   * Set the active project tab and sync ``?tab=`` (omit for Overview).
   *
   * @param {string} tabId - Tab id from ``PROJECT_TABS``.
   * @returns {void}
   */
  const setActiveTab = useCallback(
    (tabId) => {
      const nextTab = resolveProjectTab(tabId);
      setActiveTabState(nextTab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextTab === DEFAULT_PROJECT_TAB) {
            next.delete("tab");
          } else {
            next.set("tab", nextTab);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Keep state in sync when the URL tab changes (back/forward / deep link).
  useEffect(() => {
    const fromUrl = resolveProjectTab(searchParams.get("tab"));
    setActiveTabState((current) => (current === fromUrl ? current : fromUrl));
  }, [searchParams]);

  const openEditProject = useCallback(() => {
    setEditingProject(project);
  }, [project]);

  const closeEditProject = useCallback(() => {
    setEditingProject(null);
  }, []);

  const handleProjectSaved = useCallback(() => {
    setEditingProject(null);
    refreshProject();
  }, [refreshProject]);

  const value = useMemo(
    () => ({
      project,
      projectId,
      activeTab,
      setActiveTab,
      editingProject,
      openEditProject,
      closeEditProject,
      handleProjectSaved,
      isLoading,
      error,
      refreshProject,
      placeholderContent: PROJECT_PAGE_PLACEHOLDER_CONTENT,
      tmTicketSummary,
      setTmTicketSummary,
      rfiTicketSummary,
      setRfiTicketSummary,
      documentFolders,
      entireProjectBounds,
      workerHoursSummary,
      workerHoursRange,
      setWorkerHoursRangeSelection,
      setWorkerHoursTypeKeys,
      concretePoursSummary,
      concretePoursRange,
      setConcretePoursRangeSelection,
      setConcretePoursTypeKey,
    }),
    [
      project,
      projectId,
      activeTab,
      setActiveTab,
      editingProject,
      openEditProject,
      closeEditProject,
      handleProjectSaved,
      isLoading,
      error,
      refreshProject,
      tmTicketSummary,
      rfiTicketSummary,
      documentFolders,
      entireProjectBounds,
      workerHoursSummary,
      workerHoursRange,
      setWorkerHoursRangeSelection,
      setWorkerHoursTypeKeys,
      concretePoursSummary,
      concretePoursRange,
      setConcretePoursRangeSelection,
      setConcretePoursTypeKey,
    ]
  );

  return (
    <ProjectPageContext.Provider value={value}>
      {children}
    </ProjectPageContext.Provider>
  );
};

export default ProjectPageContext;
